import { object, string } from 'yup';
import { BaseModel } from '../base.model';
import { JsonObject } from '../../helpers';
import { PolicyModelV1 } from '../policy-model-v1/policy-v1.model';

export const ClassifierType = {
  Intent: 'intent',
  Custom: 'custom',
} as const;

export type ClassifierType = (typeof ClassifierType)[keyof typeof ClassifierType];

type ClassifierScope = 'organization' | 'group';

export const ClassifierSubType = {
  Domain: 'domain',
  Task: 'task',
  Action: 'action',
  Custom: 'custom',
  Regex: 'regex',
} as const;

export type ClassifierSubType = (typeof ClassifierSubType)[keyof typeof ClassifierSubType];

export type ClassifierCreateOption = 'intent' | 'regex';

export type ClassifierUsage = {
  inUse: boolean;
  policies: PolicyModelV1[];
};

export type ClassifierConfig = {
  pattern: string;
};

export class ClassifierModel extends BaseModel {
  orgId: string;
  name: string;
  description?: string;
  type: ClassifierType;
  subtype: ClassifierSubType;
  enabled: boolean;
  error: string;
  deprecated: boolean;
  scope: ClassifierScope;
  config?: ClassifierConfig;
  phrases?: string[];
  numSyntheticConversations?: number;

  schema = object({
    name: string().required('Name is required'),
  });

  constructor(props: JsonObject = {}) {
    super(props);

    const data = props as Record<keyof ClassifierModel, unknown>;

    this.orgId = (data.orgId as string) || '';
    this.name = (data.name as string) || '';
    this.description = (data.description as string) || '';
    this.type = (data.type as ClassifierType) || '';
    this.subtype = (data.subtype as ClassifierSubType) || '';
    this.enabled = !!data.enabled;
    this.error = (data.error as string) || '';
    this.deprecated = false;
    this.scope = (data.scope as ClassifierScope) || 'organization';
    this.config = (data.config as ClassifierConfig) || { pattern: '' };
    this.phrases = data.phrases as string[] | undefined;
    this.numSyntheticConversations = data.numSyntheticConversations as number | undefined;
  }

  get typeLabel(): string {
    if (this.subtype === ClassifierSubType.Custom) {
      return 'Intent';
    }
    if (this.subtype === ClassifierSubType.Regex) {
      return 'Custom Entity';
    }
    return this.subtype;
  }
}

export class CreateClassifierModel extends BaseModel {
  name: string;
  description?: string;
  enabled: boolean;
  type: ClassifierType;
  subtype: ClassifierSubType;
  config?: ClassifierConfig;
  phrases?: string[];
  numSyntheticConversations?: number;

  constructor(classifier: ClassifierModel) {
    super(classifier._props);
    this.name = classifier.name;
    this.description = classifier.description;
    this.enabled = classifier.enabled;
    this.type = classifier.type || ClassifierType.Custom;
    this.subtype = classifier.subtype || ClassifierSubType.Custom;

    if (classifier.subtype === ClassifierSubType.Regex) {
      this.config = classifier.config ?? { pattern: '' };
    }

    if (classifier.subtype === ClassifierSubType.Custom) {
      this.numSyntheticConversations = classifier.numSyntheticConversations ?? 0;
      this.phrases = classifier.phrases ?? [];
    }
  }
}
